import { ClienteDto, DatiBilancio } from "@api/client";
import { companyUpdateCompany } from "common/_redux/company/actions";
import { companyDetailsSelector } from "common/_redux/company/selectors";
import AppDatePicker from "common/components/app-date-picker";
import BftBooleanButton from "common/components/boolean-button";
import BftButton from "common/components/button";
import BftInput from "common/components/input";
import BftRadioGroup from "common/components/radio-button";
import { Formik, datiBilancioSchema } from "common/validators/validators";
import { BooleanValues } from "constants/global";
import { risultatoEconomicoOptions, volumeAffariOptions } from "constants/options";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import React from "react";
import { Col, Form, Row } from "react-bootstrap";

const { TRUE, FALSE } = BooleanValues;

interface Props {
	onComplete: () => void;
}

const BilancioImpresa: React.FC<Props> = (props) => {
	const { onComplete } = props;

	const dispatch = useAppDispatch();

	const companyDetails = useAppSelector(companyDetailsSelector);

	const _handleSubmit = (values: DatiBilancio) => {
		let request: ClienteDto = {
			...companyDetails,
			datiBilancio: values,
		};

		dispatch(companyUpdateCompany(request))
			.unwrap()
			.then((res) => {
				if (res) onComplete();
			});
	};

	function formattaEuro(numero: number | undefined) {
		return numero?.toLocaleString("it-IT", {
			maximumFractionDigits: 0,
		});
	}

	return (
		<Formik
			validationSchema={datiBilancioSchema}
			onSubmit={_handleSubmit}
			initialValues={{
				startup: companyDetails?.datiBilancio?.startup || false,
				contabilitaSemplificata: companyDetails?.datiBilancio?.contabilitaSemplificata,
				dataInizioAttivita: companyDetails?.datiBilancio?.dataInizioAttivita,
				risultatoEconomicoUltimoBilancio:
					companyDetails?.datiBilancio?.risultatoEconomicoUltimoBilancio,
				importoUltimoBilancio: companyDetails?.datiBilancio?.importoUltimoBilancio || 0,
				volumeAffari: companyDetails?.datiBilancio?.volumeAffari,
			}}
			enableReinitialize
		>
			{({ handleSubmit, handleChange, setFieldValue, values, touched, errors }) => {
				const resetBilancioFields = () => {
					setFieldValue("risultatoEconomicoUltimoBilancio", undefined);
					setFieldValue("importoUltimoBilancio", "");
					setFieldValue("volumeAffari", undefined);
				};

				const handleChangeCustom = (input: string, type?: string) => (e: any) => {
					if (input === "startup") {
						const value = e === TRUE;
						setFieldValue(input, value);
						if (value) {
							resetBilancioFields();
						}
					} else if (type === "amount") {
						const value = typeof e === "object" ? e.target?.value : e;
						const inputReplaced = value.replaceAll(".", "");

						const numberFormat = inputReplaced.trim() === "" ? "" : Number(inputReplaced);

						setFieldValue(input, numberFormat);
					} else if (type === "date") {
						setFieldValue(input, e);

						if (input === "dataInizioAttivita") {
							const selectedDate = new Date(e);
							const currentYear = new Date().getFullYear();
							if (selectedDate.getFullYear() === currentYear) {
								setFieldValue("startup", true);
								setFieldValue("risultatoEconomicoUltimoBilancio", undefined);
								setFieldValue("importoUltimoBilancio", "");
								setFieldValue("volumeAffari", undefined);
							} else {
								setFieldValue("startup", false);
							}
						}
					} else {
						const value = e === TRUE;
						setFieldValue(input, value);
					}
				};

				return (
					<Form noValidate onSubmit={handleSubmit}>
						<h6 className="container my-3">Data inizio attività</h6>

						<Row>
							<Col md={5}>
								<div className="container mb-3">
									<AppDatePicker
										label="Data*"
										name="dataInizioAttivita"
										value={values.dataInizioAttivita}
										onChange={handleChangeCustom("dataInizioAttivita", "date")}
									/>
									{errors.dataInizioAttivita && touched.dataInizioAttivita && (
										<Form.Control.Feedback className="ms-1 mt-2" type="invalid">
											{errors.dataInizioAttivita}
										</Form.Control.Feedback>
									)}
								</div>
							</Col>
						</Row>
						
						{/* <BftBooleanButton
							label="Sei una Start-up?"
							name="startup"
							value={String(values.startup)}
							onChange={handleChangeCustom("startup")}
						/>
						{errors.startup && touched.startup && (
							<Form.Control.Feedback className="ms-3" type="invalid">
								{errors.startup}
							</Form.Control.Feedback>
						)} */}

						<BftBooleanButton
							label="Sei in contabilità semplificata?"
							name="contabilitaSemplificata"
							value={String(values.contabilitaSemplificata)}
							onChange={handleChangeCustom("contabilitaSemplificata")}
						/>
						{errors.contabilitaSemplificata && touched.contabilitaSemplificata && (
							<Form.Control.Feedback className="ms-3" type="invalid">
								{errors.contabilitaSemplificata}
							</Form.Control.Feedback>
						)}

						{!values?.startup && (
							<>
								<h6 className="container my-3">Risultato economico ultimo bilancio</h6>

								<Row>
									<Col md={8}>
										<BftRadioGroup
											controlId="risultatoEconomicoUltimoBilancio"
											options={risultatoEconomicoOptions}
											onChange={handleChange}
											isInvalid={
												!!errors.risultatoEconomicoUltimoBilancio &&
												touched.risultatoEconomicoUltimoBilancio
											}
											selectedOption={values.risultatoEconomicoUltimoBilancio}
										/>
										{errors.risultatoEconomicoUltimoBilancio &&
											touched.risultatoEconomicoUltimoBilancio && (
												<Form.Control.Feedback className="ms-3" type="invalid">
													{errors.risultatoEconomicoUltimoBilancio}
												</Form.Control.Feedback>
											)}
									</Col>
								</Row>

								<Row>
									<Col md={8}>
										<BftInput
											required
											type={"text"}
											name={"importoUltimoBilancio"}
											placeholder={"Inserire importo*"}
											value={formattaEuro(values.importoUltimoBilancio)}
											icon="fa-euro-sign"
											inputStyle={{ borderLeftStyle: "none" }}
											onChange={handleChangeCustom("importoUltimoBilancio", "amount")}
											isInvalid={!!errors.importoUltimoBilancio && touched.importoUltimoBilancio}
											inputClass="shadow-none"
										/>

										{errors.importoUltimoBilancio && touched.importoUltimoBilancio && (
											<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
												{errors.importoUltimoBilancio}
											</Form.Control.Feedback>
										)}
									</Col>
								</Row>
							</>
						)}

						{!values.startup && (
							<>
								<h6 className="container my-3">Volume d'affari annuo</h6>

								<Row>
									<Col md={8}>
										<BftRadioGroup
											controlId="volumeAffari"
											options={volumeAffariOptions}
											onChange={handleChange}
											isInvalid={!!errors.volumeAffari && touched.volumeAffari}
											selectedOption={values.volumeAffari}
										/>
										{errors.volumeAffari && touched.volumeAffari && (
											<Form.Control.Feedback className="ms-3" type="invalid">
												{errors.volumeAffari}
											</Form.Control.Feedback>
										)}
									</Col>
								</Row>
							</>
						)}

						<div className="mx-2 my-4">
							<BftButton type="submit" className="btn-buffetti col-12">
								Avanti
							</BftButton>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default BilancioImpresa;
